import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH29_QUESTIONS } from "../data/neetPhysicsCh29Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 29: Semiconductor Electronics (NCERT Class 12, Ch. 14).
// "Semiconductor Electronics: Materials, Devices and Simple Circuits"
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Energy Bands & Semiconductor Types", "p-n Junction Diode", "Diode Applications — Rectifiers", "Special Purpose Diodes", "Transistors & Logic Gates"];

const SECTION_COLORS = {
  "Energy Bands & Semiconductor Types": "#2563eb",
  "p-n Junction Diode":                 "#7c3aed",
  "Diode Applications — Rectifiers":    "#dc2626",
  "Special Purpose Diodes":             "#16a34a",
  "Transistors & Logic Gates":          "#ea580c",
};

const CONCEPTS = [
  // ── Energy Bands & Semiconductor Types ──────────────────────────────────────
  { section: "Energy Bands & Semiconductor Types", title: "Energy bands in solids — valence band, conduction band and energy gap",
    explanation: "In a solid, the discrete energy levels of isolated atoms spread into continuous bands due to the overlapping of electron wavefunctions from closely packed atoms. The valence band is the range of energies occupied by the outermost (valence) electrons; it may be completely or partially filled but is never empty. The conduction band is the next higher band of allowed energies -- electrons that reach it are free to move through the solid and conduct electric current. Between the top of the valence band and the bottom of the conduction band lies the forbidden energy gap (Eg), a range of energies in which no electron of the solid can exist.",
    example: `Energy band diagram (schematic):

  -- Conduction Band (empty / partially filled) --
              |
              |  Eg  (forbidden energy gap)
              |
  -- Valence Band (filled / partially filled) --

Eg(diamond, insulator)       ~ 5.4 eV
Eg(silicon, semiconductor)   ~ 1.1 eV
Eg(germanium, semiconductor) ~ 0.7 eV
Eg(conductor)                = 0 eV (bands overlap)` },
  { section: "Energy Bands & Semiconductor Types", title: "Classification of solids by energy gap — conductors, insulators, semiconductors",
    explanation: "Solids are classified by the size of Eg and the filling of their bands. In conductors, the valence and conduction bands overlap (Eg = 0) or the conduction band is partially filled, so free electrons are available at any temperature. In insulators, Eg is large (typically greater than about 3 eV), so at ordinary temperatures virtually no electron can acquire enough thermal energy to cross into the conduction band. In semiconductors, Eg is small (roughly 0.2 to 3 eV -- about 1.1 eV for silicon), so a modest number of electrons are thermally excited across the gap at room temperature, giving conductivity between that of a conductor and an insulator.",
    example: `Material     Eg (eV)     Class
Copper        0          Conductor  (bands overlap)
Silicon       1.1        Semiconductor
Germanium     0.7        Semiconductor
Diamond       5.4        Insulator

Rule of thumb: Eg = 0            -> conductor
               Eg small (~1 eV)  -> semiconductor
               Eg large (>~3 eV) -> insulator` },
  { section: "Energy Bands & Semiconductor Types", title: "Intrinsic semiconductors and electron-hole pairs",
    explanation: "A pure semiconductor crystal (e.g. pure silicon or germanium) with no added impurity is called an intrinsic semiconductor. At absolute zero it behaves like an insulator, since the valence band is completely full and the conduction band is completely empty. At room temperature, thermal energy breaks a few covalent bonds, exciting an electron from the valence band to the conduction band; this creates a free electron in the conduction band AND leaves behind a vacancy, or hole, in the valence band. Because electrons and holes are always generated in pairs in an intrinsic semiconductor, the electron concentration equals the hole concentration, both equal to the intrinsic carrier concentration ni.",
    example: `Thermal breaking of a covalent bond in pure Si:

Si-Si-Si-Si          Si-Si-Si-Si
 |  |  |  |   heat     |  |  |  |
Si-Si-Si-Si  ---->    Si-Si Si-Si
 |  |  |  |             |  |    |
Si-Si-Si-Si           Si-Si-Si-Si
(all bonds intact)     (one broken bond:
                        free e- in CB + hole in VB)

Intrinsic semiconductor:  n(electrons) = p(holes) = ni` },
  { section: "Energy Bands & Semiconductor Types", title: "Extrinsic semiconductors — n-type (doping with a pentavalent impurity)",
    explanation: "Doping is the deliberate addition of a controlled, small amount of impurity to an intrinsic semiconductor to change its conductivity. Adding a pentavalent impurity (5 valence electrons, e.g. phosphorus, arsenic, antimony) to tetravalent silicon or germanium: four of the impurity atom's electrons form covalent bonds with neighbouring Si/Ge atoms, and the fifth electron is very loosely bound, needing only a small energy to become a free carrier. This is an n-type semiconductor: electrons are the majority charge carriers and holes are the minority carriers, though the crystal as a whole remains electrically neutral.",
    example: `Pentavalent dopant (P) in Si lattice -- donor impurity:

  Si - Si - Si
   |    |    |
  Si -  P  - Si     4 electrons of P form bonds with Si;
   |   /|    |      5th electron is loosely bound -> becomes
  Si - Si - Si      a free (donor) electron easily

n-type: majority carriers = electrons (e-)
        minority carriers = holes (h+)` },
  { section: "Energy Bands & Semiconductor Types", title: "Extrinsic semiconductors — p-type (doping with a trivalent impurity)",
    explanation: "Adding a trivalent impurity (3 valence electrons, e.g. boron, aluminium, indium, gallium) to tetravalent silicon or germanium leaves one covalent bond incomplete around each impurity atom, creating a vacancy, or hole, that readily accepts an electron from a neighbouring bond. This is a p-type semiconductor: holes are the majority charge carriers and electrons are the minority carriers; the crystal remains electrically neutral overall since the impurity atoms themselves are neutral.",
    example: `Trivalent dopant (B) in Si lattice -- acceptor impurity:

  Si - Si - Si
   |    |    |
  Si -  B  - Si     B forms only 3 bonds with Si;
   |    |    |      the 4th bond position is vacant -> a hole
  Si - Si - Si

p-type: majority carriers = holes (h+)
        minority carriers = electrons (e-)` },

  // ── p-n Junction Diode ───────────────────────────────────────────────────────
  { section: "p-n Junction Diode", title: "Formation of a p-n junction — diffusion and the depletion region",
    explanation: "A p-n junction is formed within a single semiconductor crystal by doping one side p-type and the other n-type. Near the junction, free electrons from the n-side diffuse into the p-side (where electron concentration is low) and holes from the p-side diffuse into the n-side, and they recombine. This diffusion leaves behind uncovered (immobile) negative acceptor ions on the p-side and positive donor ions on the n-side near the junction. The resulting narrow region, stripped of mobile charge carriers, is called the depletion region (or depletion layer).",
    example: `Before diffusion:            After diffusion (equilibrium):

  p-type | n-type              p-type | | n-type
 (holes) | (electrons)   ->  (- ions) |D| (+ ions)
                                      |e|
                                      |p|
                               <-- depletion region -->
                               (no free/mobile carriers here)` },
  { section: "p-n Junction Diode", title: "Barrier (built-in) potential of a p-n junction",
    explanation: "The immobile ions on either side of the depletion region set up an internal electric field directed from the n-side to the p-side, which opposes further diffusion of majority carriers across the junction. This field corresponds to a potential difference across the depletion region called the barrier potential or built-in potential. At equilibrium (no external bias), the diffusion current (majority carriers moving across due to the concentration gradient) is exactly balanced by the drift current (minority carriers swept across by the internal field), so the net current is zero.",
    example: `Typical barrier potential (built-in voltage), 300 K, no bias:

Silicon p-n junction     ~ 0.7 V
Germanium p-n junction   ~ 0.3 V

At equilibrium: Diffusion current = Drift current  ->  Inet = 0` },
  { section: "p-n Junction Diode", title: "Forward biasing of a p-n junction diode",
    explanation: "In forward bias, the p-side is connected to the positive terminal and the n-side to the negative terminal of an external battery. The applied field opposes the internal field, so the barrier potential and the width of the depletion region both decrease. This makes it easy for majority carriers to diffuse across the junction, so a large current (of the order of milliamperes) flows once the applied voltage exceeds a threshold (knee) voltage -- about 0.7 V for silicon and 0.3 V for germanium -- and the current rises rapidly (roughly exponentially) with further increase in voltage.",
    example: `Forward bias circuit:

  p-side ----|>|---- n-side
   +  battery(V)  -    (diode symbol; arrow = conventional
                         current direction, points p -> n)

  (+) terminal -> p-side,  (-) terminal -> n-side
  Effect: barrier potential DOWN, depletion width DOWN,
          current rises sharply once V > knee voltage` },
  { section: "p-n Junction Diode", title: "Reverse biasing of a p-n junction diode",
    explanation: "In reverse bias, the p-side is connected to the negative terminal and the n-side to the positive terminal of the battery. The applied field adds to the internal field, so the barrier potential and depletion width both INCREASE, making it harder for majority carriers to cross. Only a very small reverse saturation current (of the order of microamperes for silicon), due to minority carriers being swept across by the field, flows -- and it stays almost constant (independent of reverse voltage) until the breakdown voltage is reached.",
    example: `Reverse bias circuit:

  p-side ----|>|---- n-side
   -  battery(V)  +

  (-) terminal -> p-side,  (+) terminal -> n-side
  Effect: barrier potential UP, depletion width UP,
          only a tiny, ~constant reverse saturation current
          (minority-carrier current) flows` },
  { section: "p-n Junction Diode", title: "I-V characteristics of a p-n junction diode",
    explanation: "A plot of current through a diode against the voltage across it has two very different branches. In the forward-bias region, current is negligible below the knee voltage and then rises sharply (almost exponentially) with voltage. In the reverse-bias region, only a small, nearly constant reverse saturation current flows; if the reverse voltage is increased beyond the diode's breakdown voltage, the current increases suddenly and steeply -- this is exploited deliberately in Zener diodes, but can destroy an ordinary diode.",
    example: `I (current)
 |                     forward branch
 |                    / (rises sharply
 |                   /   after knee voltage)
 |__________________/________________ V (voltage)
 |  reverse branch  |  0   knee (~0.7 V, Si)
 |  (tiny, ~flat)    |
 |                   |
 |__ breakdown ------|  (sudden current rise
     region               in reverse bias)` },

  // ── Diode Applications — Rectifiers ─────────────────────────────────────────
  { section: "Diode Applications — Rectifiers", title: "Diode as a rectifier — the principle of rectification",
    explanation: "A p-n junction diode conducts easily when forward biased but blocks current almost completely when reverse biased -- this one-way conduction property is used to convert alternating current (AC), which reverses direction periodically, into direct current (DC), which flows in one direction only. This process is called rectification, and a circuit that performs it is called a rectifier.",
    example: `AC input (sine wave):          Diode allows current only
   +   /\\      /\\               when it is forward biased,
      /  \\    /  \\              i.e. only during the half
  0--/----\\--/----\\--> t        cycle where the diode's
         \\/                     anode is at higher potential
   -                             than its cathode` },
  { section: "Diode Applications — Rectifiers", title: "Half-wave rectifier — circuit and output waveform",
    explanation: "A half-wave rectifier uses a single diode in series with the load. During the half cycle of the AC input when the diode is forward biased, it conducts and current flows through the load; during the opposite half cycle the diode is reverse biased and blocks current, so no current flows through the load. The output is thus a series of one-directional pulses appearing only during alternate half cycles of the input, with the load carrying no current at all during the other half -- this is pulsating DC with a large gap between pulses.",
    example: `Circuit:  AC ---- [diode] ----+---- Load ----+---- back to AC

Input (AC):     /\\      /\\      /\\
               /  \\    /  \\    /  \\
           ---/----\\--/----\\--/----\\--- t
             \\    /    \\    /    \\
              \\  /      \\  /      \\

Output (half-wave):  __      __      __
                    /  \\    /  \\    /  \\
                ---/----\\--/----\\--/----\\--- t
                  (only ONE half-cycle per input cycle
                   appears at the output; other half = 0)` },
  { section: "Diode Applications — Rectifiers", title: "Full-wave rectifier — center-tap configuration",
    explanation: "A full-wave center-tap rectifier uses a transformer with a center-tapped secondary winding and two diodes. During one half cycle of the input, one diode is forward biased and conducts, driving current through the load; during the other half cycle, the second diode is forward biased instead, driving current through the load in the SAME direction as before (because of how the center tap and diode orientations are arranged). As a result, current flows through the load during both halves of every input cycle, doubling the number of output pulses per cycle compared to a half-wave rectifier.",
    example: `Center-tapped secondary:        D1
        A o---------------|>|-----+
             |                    |
   center-tap o (return path)   Load
             |                    |
        B o---------------|>|-----+
                            D2

Half-cycle 1: D1 conducts (D2 reverse biased) -> current thru load
Half-cycle 2: D2 conducts (D1 reverse biased) -> current thru load
   (same direction through the load both times)
Output: TWO pulses per input cycle (both half-cycles used)` },
  { section: "Diode Applications — Rectifiers", title: "Full-wave bridge rectifier",
    explanation: "A bridge rectifier uses four diodes arranged in a bridge configuration and needs only an ordinary (non-center-tapped) transformer secondary. During one half cycle of the input, one diagonal pair of diodes is forward biased and conducts, sending current through the load in a fixed direction; during the other half cycle, the other diagonal pair conducts instead, again sending current through the load in the SAME direction. Like the center-tap rectifier, it produces two output pulses per input cycle, but without needing a center-tapped transformer.",
    example: `         D1        D2
    A o--|>|--+--+--|<|--o B
              |  |
           Load (current always flows
              |    through load in one fixed
    A o--|<|--+--+--|>|--o B   direction, regardless of
         D4        D3          the AC input's half-cycle)

Half-cycle 1: D1 & D3 conduct  (D2, D4 reverse biased)
Half-cycle 2: D2 & D4 conduct  (D1, D3 reverse biased)
Output: TWO pulses per input cycle, no center tap needed` },
  { section: "Diode Applications — Rectifiers", title: "Ripple and the role of a filter capacitor",
    explanation: "The direct output of a half-wave or full-wave rectifier is 'pulsating DC' -- it flows in one direction but its magnitude varies periodically; this unwanted AC variation riding on the DC level is called ripple. A capacitor connected in parallel with the load (a filter capacitor) smooths this out: it charges up toward the peak voltage when the rectified voltage is rising, and then discharges slowly through the load when the rectified voltage falls (and the diode is not conducting), keeping the output voltage much closer to a constant DC level and reducing the ripple.",
    example: `Without filter capacitor:        With filter capacitor:
  __      __      __              ______________
 /  \\    /  \\    /  \\            /  \`--__\`--__ (smoother,
/----\\--/----\\--/----\\--      --/---------------  nearly-flat
     (large ripple)                (capacitor supplies load     DC with
                                     current while diode is       small
                                     momentarily non-conducting)  ripple)` },

  // ── Special Purpose Diodes ──────────────────────────────────────────────────
  { section: "Special Purpose Diodes", title: "Zener diode — reverse breakdown mechanisms",
    explanation: "A Zener diode is a heavily doped p-n junction diode specially designed to operate safely in the reverse breakdown region. Two mechanisms can cause reverse breakdown: Zener breakdown, which occurs in heavily doped junctions (thin depletion layer) where a strong electric field directly breaks covalent bonds, releasing charge carriers (dominant at lower breakdown voltages, typically below about 6 V); and avalanche breakdown, which occurs in more lightly doped junctions (wider depletion layer) where minority carriers accelerated by the field collide with atoms and knock loose more carriers in a multiplying chain reaction (dominant at higher breakdown voltages). Once in breakdown, the voltage across the diode stays almost constant over a wide range of current.",
    example: `Zener diode symbol:  ---|>|---
                          Z     (bent cathode bar)

Reverse breakdown voltage Vz stays nearly CONSTANT
even as reverse current through the diode changes
substantially -- this near-constant Vz is what makes
a Zener diode useful as a voltage reference/regulator` },
  { section: "Special Purpose Diodes", title: "Zener diode as a voltage regulator",
    explanation: "A Zener diode is connected in reverse bias, in parallel with the load, fed through a series resistor Rs from an unregulated DC supply. As long as the Zener stays within its breakdown region, the voltage across it (and hence across the load, since they are in parallel) remains essentially fixed at Vz, even if the input voltage fluctuates or the load current changes -- any excess voltage is dropped across Rs, and any change in current is absorbed by the Zener diode itself. This provides a simple, stable regulated DC output.",
    example: `Unregulated       Rs
  supply  o------/\\/\\/\\------+-------+
   Vin                        |       |
                              ---     Load
                          Zener| |    (Vout = Vz,
                          (rev.)|     stays ~constant
                              ---     even if Vin or
                               |      load current
   0V ------------------------+-------+   varies)` },
  { section: "Special Purpose Diodes", title: "Light Emitting Diode (LED) — working principle",
    explanation: "An LED is a heavily doped p-n junction diode, made from a suitable compound semiconductor (such as gallium arsenide or gallium phosphide), that emits light when it is forward biased. Forward bias injects a large number of electrons and holes across the junction; when these recombine, the excess energy is released as photons of light (rather than as heat, as happens in an ordinary silicon diode). The wavelength (colour) of the emitted light depends on the energy gap of the semiconductor material used. LEDs operate at low voltage, consume little power, and respond fast -- they emit no light at all in reverse bias.",
    example: `Forward-biased LED: electron (CB) recombines with hole (VB)
                     at/near the junction
                     -> releases energy as a photon (hf ~ Eg)

hf = Eg   ->   larger Eg  -> shorter wavelength (bluer light)
              smaller Eg -> longer wavelength (redder light)` },
  { section: "Special Purpose Diodes", title: "Photodiode and solar cell — working principle",
    explanation: "A photodiode is a p-n junction diode operated in reverse bias and kept in a package that lets light fall on the junction; photons with energy greater than the energy gap generate electron-hole pairs near the junction, and the internal field sweeps these apart, increasing the reverse current in proportion to the intensity of the incident light -- so it is used to detect light and measure its intensity. A solar cell is also a p-n junction, but it is operated with NO external bias at all: light falling on it generates electron-hole pairs, and the junction field separates them (electrons collect on the n-side, holes on the p-side), so the junction itself acts as a source of emf, directly converting light energy into electrical energy.",
    example: `Photodiode (reverse biased):        Solar cell (no bias):
 light -> generates e-h pairs         light -> generates e-h pairs
 -> reverse current INCREASES         -> junction field separates
    with light intensity                 them -> a photo-emf/
 (used as a light sensor)                photocurrent appears
                                       (used as a power source)` },

  // ── Transistors & Logic Gates ────────────────────────────────────────────────
  { section: "Transistors & Logic Gates", title: "Transistor — basic structure (npn / pnp) and biasing",
    explanation: "A bipolar junction transistor has three doped regions and two junctions, forming either an n-p-n or a p-n-p sandwich, with three terminals: emitter (E), base (B), and collector (C). The emitter is heavily doped (to supply a large number of charge carriers), the base is thin and lightly doped, and the collector is moderately doped and physically larger (to dissipate heat and collect carriers). In normal (active-region) operation, the emitter-base junction is forward biased and the collector-base junction is reverse biased.",
    example: `npn transistor:              pnp transistor:
  E(n) | B(p) | C(n)            E(p) | B(n) | C(p)
  --------------------          --------------------
  heavily  thin,   moderately    heavily  thin,   moderately
  doped    lightly doped,        doped    lightly doped,
           doped    larger                doped    larger

Normal operation (both types):
  Emitter-Base junction   -> forward biased
  Collector-Base junction -> reverse biased` },
  { section: "Transistors & Logic Gates", title: "Transistor action — how current flows through a transistor",
    explanation: "Consider an npn transistor: the forward-biased emitter-base junction injects a large number of electrons from the emitter into the thin base. Because the base is thin and lightly doped, only a small fraction of these electrons recombine there (constituting the small base current IB); the vast majority diffuse straight across the base and are swept into the collector by the reverse-biased collector-base junction's field, constituting the collector current IC. By Kirchhoff's current law applied to the transistor, IE = IB + IC, and since IB is very small, IC is only slightly less than IE.",
    example: `npn transistor currents:

IE (emitter current, INTO the emitter)
   = IB (base current, small, OUT of the base)
   + IC (collector current, OUT of the collector, IC ~ IE)

IE = IB + IC     (IB << IC; IB is typically a few % of IE)` },
  { section: "Transistors & Logic Gates", title: "Transistor as an amplifier (common-emitter configuration)",
    explanation: "In a common-emitter (CE) amplifier, a small, varying input signal is applied between the base and emitter, causing small changes in the base current IB; because the collector current is related to the base current by the current gain, IC = beta x IB, with beta typically in the range of tens to a few hundred, a small change in IB produces a MUCH larger change in IC. This larger current, flowing through a collector-side load resistor, produces a large varying voltage there -- so a weak input signal is reproduced at the output as a much stronger (amplified) signal, phase-shifted by 180 degrees relative to the input in the CE configuration.",
    example: `Current gain (common-emitter): beta = IC / IB   (typically 20-200+)

Small change in base current:            Delta_IB
Resulting change in collector current:   Delta_IC = beta x Delta_IB

Example: beta = 100, Delta_IB = 10 microA
         Delta_IC = 100 x 10 microA = 1000 microA = 1 mA
(a tiny base-current change gives a much bigger
 collector-current change -> current/voltage amplification)` },
  { section: "Transistors & Logic Gates", title: "Transistor as a switch, and logic gates — AND, OR, NOT, NAND, NOR",
    explanation: "When operated at the extremes of its characteristics rather than in the linear active region, a transistor behaves like a switch: in cutoff (base current is essentially zero) it conducts almost no collector current and behaves like an OPEN switch; in saturation (a large enough base current is supplied that both junctions are forward biased) it conducts the maximum collector current the external circuit allows, with a very small voltage drop across it, behaving like a CLOSED switch. This two-state (ON/OFF) behaviour is the building block of digital logic gates, which combine HIGH (1) and LOW (0) input voltage levels according to a fixed rule.",
    example: `Basic logic gates (2-input, except NOT):

AND:  A B | Y      OR:   A B | Y      NOT:  A | Y
      0 0 | 0             0 0 | 0            0 | 1
      0 1 | 0             0 1 | 1            1 | 0
      1 0 | 0             1 0 | 1
      1 1 | 1             1 1 | 1

NAND (NOT-AND):  A B | Y    NOR (NOT-OR):   A B | Y
                 0 0 | 1                    0 0 | 1
                 0 1 | 1                    0 1 | 0
                 1 0 | 1                    1 0 | 0
                 1 1 | 0                    1 1 | 0

NAND and NOR are called "universal gates" -- ANY logic
gate/function can be built using only NAND gates (or
only NOR gates).` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh29Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh29Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh29Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH29_QUESTIONS : NEET_PHYSICS_CH29_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 29</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Semiconductor Electronics</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12, Ch. 14 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH29_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH29_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
